import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Badge, Button } from 'react-bootstrap';

// The catalog's own wording when there is one, because a variable-credit
// course ("1-15") stores only its lower bound as a number.
export function creditsText(course) {
    const label = course.creditsLabel || course.credits;
    if (label === undefined || label === null || label === '') return null;
    return `${label} credit${String(label) === '1' ? '' : 's'}`;
}

// Everything the catalog says about one course, untruncated. The card clamps
// the description to keep the grid even; this is where it is read in full.
// The host supplies the eligibility badges and the action buttons, so what a
// role may do from here stays defined in one place, next to the card's own
// buttons.
function CourseDetailModal({
    course = null,
    onHide,
    eligibility = null,
    actions = null,
}) {
    const titleId = 'course-detail-title';
    // Closing sets the course to null, but the dialog is still fading out;
    // the last course shown keeps its content in place until it is gone.
    const lastShownRef = useRef({ course, eligibility, actions });
    if (course) lastShownRef.current = { course, eligibility, actions };
    const {
        course: shown,
        eligibility: shownEligibility,
        actions: shownActions,
    } = course ? { course, eligibility, actions } : lastShownRef.current;
    return (
        <Modal
            show={Boolean(course)}
            onHide={onHide}
            aria-labelledby={titleId}
            centered
        >
            {shown && (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title id={titleId} className="h5">
                            <Badge bg="secondary" className="me-2 align-middle">
                                {shown.subject} {shown.courseNumber}
                            </Badge>
                            {shown.title}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <dl className="row small mb-3">
                            {creditsText(shown) && (
                                <>
                                    <dt className="col-sm-4">Credits</dt>
                                    <dd className="col-sm-8">
                                        {creditsText(shown)}
                                    </dd>
                                </>
                            )}
                            {shown.generalStudies && (
                                <>
                                    <dt className="col-sm-4">
                                        General studies
                                    </dt>
                                    <dd className="col-sm-8">
                                        {shown.generalStudies}
                                    </dd>
                                </>
                            )}
                            {shown.guaranteedTransfer && (
                                <>
                                    <dt className="col-sm-4">
                                        Guaranteed transfer
                                    </dt>
                                    <dd className="col-sm-8">
                                        {shown.guaranteedTransfer}
                                    </dd>
                                </>
                            )}
                            {shownEligibility && (
                                <>
                                    <dt className="col-sm-4">Seeking</dt>
                                    <dd className="col-sm-8">
                                        {shownEligibility}
                                    </dd>
                                </>
                            )}
                        </dl>
                        {shown.description ? (
                            <p className="course-detail-description mb-3">
                                {shown.description}
                            </p>
                        ) : (
                            <p className="text-muted mb-3">
                                The catalog has no description for this course.
                            </p>
                        )}
                        {shown.catalogUrl && (
                            <a
                                href={shown.catalogUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small"
                            >
                                View in catalog
                                <span className="visually-hidden">
                                    {' '}
                                    for {shown.subject} {shown.courseNumber}{' '}
                                    (opens in a new tab)
                                </span>
                            </a>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-primary" onClick={onHide}>
                            Close
                        </Button>
                        {shownActions}
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
}

CourseDetailModal.propTypes = {
    course: PropTypes.shape({
        subject: PropTypes.string,
        courseNumber: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        catalogUrl: PropTypes.string,
        generalStudies: PropTypes.string,
        guaranteedTransfer: PropTypes.string,
    }),
    onHide: PropTypes.func.isRequired,
    eligibility: PropTypes.node,
    actions: PropTypes.node,
};

export default CourseDetailModal;
